import { useEffect, useState } from "react";
import {
  findAllLotacoes,
  findAllUnidadesRegionais,
  findAllDiretorias,
  findAllSuperintendencias,
  findAllInstituicoesEstaduais,
  findAllAreasAtuacao,
  findAllOrgaosAtuacao,
  findAllFuncoesDesempenhadas,
  findAllVinculosEmpregaticios,
} from "../API/apiDadosComplementares";
import { useDadoComplementarStore } from "../store/useDadoComplementarStore";
import { convertListSelect } from "../utils/Arrays";

let dadosComplementaresPromise: Promise<void> | null = null;

export const useDadosComplementares = () => {
  const [msgError, setMsgError] = useState<string | null>(null);

  const {
    loadindDadosCompl,
    dadosComplementaresCarregados,
    setLoadingDadosCompl,
    setDadosComplementaresCarregados,
    setLotacoes,
    setUnidades,
    setDiretorias,
    setDiretoriasRegionais,
    setDiretoriasInternas,
    setSuperintendencias,
    setInstituicoesEstaduais,
    setAreasAtuacao,
    setOrgaosAtuacao,
    setVinculos,
    setFuncoes,
    setFuncoesDesempenhadas,
  } = useDadoComplementarStore();

  useEffect(() => {
    carregaDadosComplementares();
  }, []);

  const carregaDadosComplementares = async (force = false) => {
    if (!force && dadosComplementaresCarregados) {
      return;
    }

    if (loadindDadosCompl && dadosComplementaresPromise) {
      await dadosComplementaresPromise;
      return;
    }

    setLoadingDadosCompl(true);

    dadosComplementaresPromise = (async () => {
      const results = await Promise.all([
        findLotacoes(),
        findUnidadesRegionais(),
        findDiretorias(),
        findSuperintendencias(),
        findInstituicoesEstaduais(),
        findAreasAtuacao(),
        findOrgaosAtuacao(),
        findFuncoesDesempenhadas(),
        findVinculosEmpregaticios(),
      ]);

      const hasErrors = results.some((success) => !success);
      setDadosComplementaresCarregados(!hasErrors);
    })()
      .finally(() => {
        setLoadingDadosCompl(false);
        dadosComplementaresPromise = null;
      });

    await dadosComplementaresPromise;
  };

  const findLotacoes = async () => {
    try {
      const result = await findAllLotacoes();

      if (!result) {
        setMsgError("Nao foi possivel encontrar as lotacoes");
      }

      const lotacoes = convertListSelect(result, "id", "nome");
      setLotacoes(lotacoes);
      return true;
    } catch (error) {
      setMsgError("Lotacoes nao encontradas");
      return false;
    }
  };

  const findUnidadesRegionais = async () => {
    try {
      const result = await findAllUnidadesRegionais();

      if (!result) {
        setMsgError("Nao foi possivel encontrar as unidades regionais");
      }

      const unidades = convertListSelect(result, "id", "nome");
      setUnidades(unidades);
      return true;
    } catch (error) {
      setMsgError("Unidades regionais nao encontradas");
      return false;
    }
  };

  const findDiretorias = async () => {
    try {
      const allResult = await findAllDiretorias();

      if (!allResult) {
        setMsgError("Nao foi possivel encontrar as diretorias");
      }

      setDiretorias(allResult);
      setDiretoriasRegionais(
        allResult.filter((item) => item.tipo?.toUpperCase() === "REGIONAL"),
      );
      setDiretoriasInternas(
        allResult.filter((item) =>
          ["INTERNA", "VINCULADA"].includes(item.tipo?.toUpperCase()),
        ),
      );
      return true;
    } catch (error) {
      setMsgError("Diretorias nao encontradas");
      return false;
    }
  };

  const findSuperintendencias = async () => {
    try {
      const result = await findAllSuperintendencias();

      if (!result) {
        setMsgError("Nao foi possivel encontrar as superintendencias");
      }

      const superintendencias = convertListSelect(result, "id", "nome");
      setSuperintendencias(superintendencias);
      return true;
    } catch (error) {
      setMsgError("Superintendencias nao encontradas");
      return false;
    }
  };

  const findInstituicoesEstaduais = async () => {
    try {
      const result = await findAllInstituicoesEstaduais();

      if (!result) {
        setMsgError("Nao foi possivel encontrar as instituicoes estaduais");
      }

      const instituicoes = convertListSelect(result, "id", "nome");
      setInstituicoesEstaduais(instituicoes);
      return true;
    } catch (error) {
      setMsgError("Instituicoes estaduais nao encontradas");
      return false;
    }
  };

  const findAreasAtuacao = async () => {
    try {
      const result = await findAllAreasAtuacao();

      if (!result) {
        setMsgError("Nao foi possivel encontrar as areas de atuacao");
      }

      setAreasAtuacao(result);
      return true;
    } catch (error) {
      setMsgError("Areas de atuacao nao encontradas");
      return false;
    }
  };

  const findOrgaosAtuacao = async () => {
    try {
      const result = await findAllOrgaosAtuacao();

      if (!result) {
        setMsgError("Nao foi possivel encontrar os orgaos de atuacao");
      }

      setOrgaosAtuacao(result);
      return true;
    } catch (error) {
      setMsgError("Orgaos de atuacao nao encontrados");
      return false;
    }
  };

  const findFuncoesDesempenhadas = async () => {
    try {
      const result = await findAllFuncoesDesempenhadas();

      if (!result) {
        setMsgError("Nao foi possivel encontrar as funcoes desempenhadas");
      }

      setFuncoes(result);
      setFuncoesDesempenhadas(result);
      return true;
    } catch (error) {
      setMsgError("Funcoes desempenhadas nao encontradas");
      return false;
    }
  };

  const findVinculosEmpregaticios = async () => {
    try {
      const result = await findAllVinculosEmpregaticios();

      if (!result) {
        setMsgError("Nao foi possivel encontrar os vinculos empregaticios");
      }

      setVinculos(result);
      return true;
    } catch (error) {
      setMsgError("Vinculos empregaticios nao encontrados");
      return false;
    }
  };

  return {
    msgError,
    carregaDadosComplementares,
  };
};
